import { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useVendorStore } from '../stores/vendorStore';
import { exportVendorFile, exportAllBanksFile, exportFilteredFile, exportBanksByIds } from '../services/api';
import Navbar from '../components/Navbar';
import AddBankDialogEnhanced from '../components/AddBankDialogEnhanced';
import BankDetailDialogEnhanced from '../components/BankDetailDialogEnhanced';
import UploadBankDialog from '../components/UploadBankDialog';
import PinDialog from '../components/PinDialog';
import VendorList from '../components/vendors/VendorList';
import BankTable from '../components/vendors/BankTable';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '../components/ui/dropdown-menu';
import { Plus, Search, FileSpreadsheet, FileText, Upload, Download } from 'lucide-react';
import { toast } from 'sonner';

const checkPricingMissing = (bank) => {
  return !bank.pricingType || (
    (bank.pricingType === 'Rent' && !bank.rentAmount) ||
    (bank.pricingType === 'Commission' && !bank.commissionRate) ||
    (bank.pricingType === 'Both' && (!bank.rentAmount || !bank.commissionRate)) ||
    (bank.pricingType === 'Purchased' && !bank.purchaseAmount)
  );
};

const formatMoney = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

const downloadBlob = (data, filename, format) => {
  const mimeType = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const blob = new Blob([data], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const VendorsPage = () => {
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'Admin' || user?.role === 'Super Admin';
  const { vendors, selectedVendor, loading, fetchVendors, setSelectedVendor, setSelectedBank } = useVendorStore();

  const [showVendorDialog, setShowVendorDialog] = useState(false);
  const [showBankDialog, setShowBankDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showBankDetailDialog, setShowBankDetailDialog] = useState(false);
  const [vendorForm, setVendorForm] = useState({ name: '' });
  const [editingVendor, setEditingVendor] = useState(null);
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pinLoading, setPinLoading] = useState(false);
  const [vendorSearch, setVendorSearch] = useState('');
  const [bankFilters, setBankFilters] = useState({ status: '', pricingStatus: '', search: '', qrFilter: '', branch: '' });
  const [bankSort, setBankSort] = useState('companyName-asc');
  const [selectedBranch, setSelectedBranch] = useState(null);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  const handleVendorSubmit = async (e) => {
    e.preventDefault();
    try {
      const store = useVendorStore.getState();
      if (editingVendor) { await store.updateVendor(editingVendor.id, vendorForm); toast.success('Vendor updated'); }
      else { await store.createVendor(vendorForm); toast.success('Vendor created'); }
      setShowVendorDialog(false); setEditingVendor(null); setVendorForm({ name: '' });
    } catch (error) { toast.error(error.response?.data?.detail || 'Operation failed'); }
  };

  const handleDeleteVendor = (vendorId) => {
    if (user?.role !== 'Admin') { toast.error('Only Admin can delete vendors'); return; }
    if (!user?.hasDeletePin) { toast.error('Please set a Delete PIN first'); return; }
    setPendingDelete({ type: 'vendor', id: vendorId }); setShowPinDialog(true);
  };

  const handleDeleteBank = (bankId) => {
    if (user?.role !== 'Admin') { toast.error('Only Admin can delete banks'); return; }
    if (!user?.hasDeletePin) { toast.error('Please set a Delete PIN first'); return; }
    setPendingDelete({ type: 'bank', id: bankId }); setShowPinDialog(true);
  };

  const handlePinConfirm = async (pin) => {
    if (!pendingDelete) return;
    setPinLoading(true);
    try {
      const store = useVendorStore.getState();
      if (pendingDelete.type === 'vendor') { await store.deleteVendor(pendingDelete.id, pin); toast.success('Vendor deleted'); }
      else { await store.deleteBank(selectedVendor.id, pendingDelete.id, pin); toast.success('Bank deleted'); }
      setShowPinDialog(false); setPendingDelete(null);
    } catch (error) { toast.error(error.response?.data?.detail || 'Delete failed'); }
    finally { setPinLoading(false); }
  };

  const handleExportVendor = async (format) => {
    if (!selectedVendor) return;
    // If any filters are active, export only the filtered subset.
    const hasFilters = !!(selectedBranch || bankFilters.status || bankFilters.branch || bankFilters.pricingStatus || bankFilters.qrFilter || bankFilters.search);
    try {
      if (hasFilters) {
        const ids = filteredBanks.map(b => b.id);
        if (ids.length === 0) { toast.error('No banks match the current filters'); return; }
        const parts = [`Vendor: ${selectedVendor.name}`];
        if (selectedBranch) parts.push(`Branch: ${selectedBranch}`);
        else if (bankFilters.branch) parts.push(`Branch: ${bankFilters.branch}`);
        if (bankFilters.status) parts.push(`Status: ${bankFilters.status}`);
        if (bankFilters.pricingStatus) parts.push(`Pricing: ${bankFilters.pricingStatus}`);
        if (bankFilters.qrFilter) parts.push(`QR: ${bankFilters.qrFilter === 'with-qr' ? 'With QR' : 'Without QR'}`);
        if (bankFilters.search) parts.push(`Search: "${bankFilters.search}"`);
        const label = parts.join(', ');
        const res = await exportBanksByIds(ids, format, label);
        const fname = `${selectedVendor.name}_filtered_${ids.length}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
        downloadBlob(res.data, fname, format);
        toast.success(`${format.toUpperCase()} exported (${ids.length} banks)`);
      } else {
        const res = await exportVendorFile(selectedVendor.id, format);
        downloadBlob(res.data, `${selectedVendor.name}_Banks.${format === 'pdf' ? 'pdf' : 'xlsx'}`, format);
        toast.success(`${format.toUpperCase()} exported`);
      }
    } catch (err) { toast.error(err?.response?.data?.detail || 'Export failed'); }
  };

  const handleExportAll = async (format) => {
    try {
      const res = await exportAllBanksFile(format);
      downloadBlob(res.data, `All_Banks.${format === 'pdf' ? 'pdf' : 'xlsx'}`, format);
      toast.success(`${format.toUpperCase()} exported`);
    } catch { toast.error('Export failed'); }
  };

  const handleExportFiltered = async (filterType, format) => {
    try {
      const res = await exportFilteredFile(filterType, format);
      downloadBlob(res.data, `${filterType}_Banks.${format === 'pdf' ? 'pdf' : 'xlsx'}`, format);
      toast.success(`${filterType} ${format.toUpperCase()} exported`);
    } catch { toast.error('Export failed'); }
  };

  const filteredVendors = vendors
    .filter(v => v.name.toLowerCase().includes(vendorSearch.toLowerCase()))
    .sort((a, b) => (b.banks?.length || 0) - (a.banks?.length || 0));

  const getFilteredBanks = () => {
    if (!selectedVendor) return [];
    let result = selectedVendor.banks.filter(bank => {
      // Branch filter from sidebar
      if (selectedBranch && (bank.assignedPartnerName || '') !== selectedBranch) return false;
      if (bankFilters.status) {
        if (bankFilters.status === 'Freezed - Transaction') {
          if (!bank.status?.startsWith('Freezed')) return false;
        } else if (bank.status !== bankFilters.status) return false;
      }
      if (bankFilters.branch && (bank.assignedPartnerName || '') !== bankFilters.branch) return false;
      if (bankFilters.pricingStatus) {
        const isMissing = checkPricingMissing(bank);
        if (bankFilters.pricingStatus === 'missing' && !isMissing) return false;
        if (bankFilters.pricingStatus === 'set' && isMissing) return false;
      }
      if (bankFilters.qrFilter) {
        if (bankFilters.qrFilter === 'with-qr' && bank.qrAvailable !== 'yes') return false;
        if (bankFilters.qrFilter === 'without-qr' && bank.qrAvailable === 'yes') return false;
      }
      if (bankFilters.search) {
        const s = bankFilters.search.toLowerCase();
        const searchable = [bank.bankName, bank.companyName, bank.region, bank.accountNumber, bank.ifsc, bank.upi, bank.status, bank.holderName, bank.accountType].join(' ').toLowerCase();
        if (!searchable.includes(s)) return false;
      }
      return true;
    });
    // Sort
    const [sortField, sortDir] = bankSort.split('-');
    result.sort((a, b) => {
      let va = '', vb = '';
      if (sortField === 'companyName') { va = a.companyName || ''; vb = b.companyName || ''; }
      else if (sortField === 'bankName') { va = a.bankName || ''; vb = b.bankName || ''; }
      else if (sortField === 'status') { va = a.status || ''; vb = b.status || ''; }
      else if (sortField === 'createdAt') { va = a.createdAt || ''; vb = b.createdAt || ''; }
      else if (sortField === 'accountNumber') { va = a.accountNumber || ''; vb = b.accountNumber || ''; }
      const cmp = va.localeCompare(vb, undefined, { numeric: true });
      return sortDir === 'desc' ? -cmp : cmp;
    });
    return result;
  };

  const filteredBanks = getFilteredBanks();
  const bankCounts = {
    total: filteredBanks.length,
    active: filteredBanks.filter(b => b.status === 'Active').length,
    freezed: filteredBanks.filter(b => b.status?.startsWith('Freezed')).length,
    freezedAmount: filteredBanks.filter(b => b.status?.startsWith('Freezed')).reduce((sum, b) => sum + (b.freezedAmount || 0), 0),
    pricingMissing: filteredBanks.filter(b => checkPricingMissing(b)).length
  };

  if (loading) {
    return (<><Navbar /><div className="flex items-center justify-center min-h-[calc(100vh-4rem)]"><div className="text-lg">Loading vendors...</div></div></>);
  }

  return (
    <>
      <Navbar />
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" data-testid="vendors-page-title">Vendors & Banks</h1>
            <p className="text-slate-600 mt-1">Manage vendors and their bank accounts</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {isAdmin && (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-1" data-testid="export-dropdown"><Download className="w-4 h-4" />Export</Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    <DropdownMenuLabel>All Banks</DropdownMenuLabel>
                    <DropdownMenuItem onClick={() => handleExportAll('pdf')} data-testid="export-all-pdf"><FileText className="w-4 h-4 mr-2" />All Banks PDF</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExportAll('excel')} data-testid="export-all-excel"><FileSpreadsheet className="w-4 h-4 mr-2" />All Banks Excel</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>By Status</DropdownMenuLabel>
                    <DropdownMenuItem onClick={() => handleExportFiltered('assigned', 'pdf')}><FileText className="w-4 h-4 mr-2" />Assigned PDF</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExportFiltered('assigned', 'excel')}><FileSpreadsheet className="w-4 h-4 mr-2" />Assigned Excel</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExportFiltered('unassigned', 'pdf')}><FileText className="w-4 h-4 mr-2" />Unassigned PDF</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExportFiltered('unassigned', 'excel')}><FileSpreadsheet className="w-4 h-4 mr-2" />Unassigned Excel</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExportFiltered('active', 'pdf')}><FileText className="w-4 h-4 mr-2" />Active PDF</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExportFiltered('active', 'excel')}><FileSpreadsheet className="w-4 h-4 mr-2" />Active Excel</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExportFiltered('inactive', 'pdf')}><FileText className="w-4 h-4 mr-2" />Inactive PDF</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExportFiltered('inactive', 'excel')}><FileSpreadsheet className="w-4 h-4 mr-2" />Inactive Excel</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExportFiltered('freezed', 'pdf')}><FileText className="w-4 h-4 mr-2" />Freezed PDF</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExportFiltered('freezed', 'excel')}><FileSpreadsheet className="w-4 h-4 mr-2" />Freezed Excel</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
            {(isAdmin || user?.canUpload) && (
              <Button onClick={() => setShowUploadDialog(true)} variant="outline" size="sm" className="gap-1" data-testid="upload-excel-btn"><Upload className="w-4 h-4" /> Upload Excel</Button>
            )}
            {(isAdmin || user?.role === 'Sub Admin') && (
              <Button onClick={() => { setEditingVendor(null); setVendorForm({ name: '' }); setShowVendorDialog(true); }} variant="outline" className="gap-2"><Plus className="w-4 h-4" /> Add Vendor</Button>
            )}
            <Button onClick={() => setShowBankDialog(true)} className="gap-2" data-testid="add-bank-btn"><Plus className="w-4 h-4" /> Add Bank</Button>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Vendors ({filteredVendors.length})</CardTitle>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input placeholder="Search vendors..." value={vendorSearch} onChange={(e) => setVendorSearch(e.target.value)} className="pl-9" />
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <VendorList
                  vendors={filteredVendors} selectedVendor={selectedVendor} setSelectedVendor={setSelectedVendor}
                  selectedBranch={selectedBranch} setSelectedBranch={setSelectedBranch}
                  activeStatusFilter={bankFilters.status}
                  onStatusFilter={(status) => setBankFilters(f => ({ ...f, status, branch: '' }))}
                  isAdmin={isAdmin} userRole={user?.role}
                  onEdit={(v) => { setEditingVendor(v); setVendorForm({ name: v.name }); setShowVendorDialog(true); }}
                  onDelete={handleDeleteVendor}
                />
              </CardContent>
            </Card>
          </div>

          <div className="col-span-12 lg:col-span-8">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{selectedVendor ? selectedVendor.name : 'Select a Vendor'}{selectedBranch ? ` / ${selectedBranch}` : ''}</CardTitle>
                    <p className="text-sm text-slate-600 mt-1">
                      {selectedVendor ? (selectedBranch ? `Banks assigned to ${selectedBranch}` : 'Bank accounts and details') : 'Choose a vendor to view banks'}
                      {selectedBranch && <button className="ml-2 text-xs text-blue-600 hover:underline" onClick={() => { setSelectedBranch(null); setBankFilters(f => ({ ...f, branch: '' })); }}>Clear branch filter</button>}
                    </p>
                  </div>
                  {selectedVendor && (() => {
                    const hasFilters = !!(selectedBranch || bankFilters.status || bankFilters.branch || bankFilters.pricingStatus || bankFilters.qrFilter || bankFilters.search);
                    const count = filteredBanks.length;
                    const excelLabel = hasFilters ? `Excel (${count})` : 'Excel';
                    const pdfLabel = hasFilters ? `PDF (${count})` : 'PDF';
                    const titleHint = hasFilters ? `Export ${count} filtered banks` : `Export all banks of ${selectedVendor.name}`;
                    return (
                      <div className="flex gap-2">
                        <Button onClick={() => handleExportVendor('excel')} variant="outline" size="sm" className="gap-1" title={titleHint} data-testid="export-vendor-excel"><FileSpreadsheet className="w-4 h-4" />{excelLabel}</Button>
                        <Button onClick={() => handleExportVendor('pdf')} variant="outline" size="sm" className="gap-1" title={titleHint} data-testid="export-vendor-pdf"><FileText className="w-4 h-4" />{pdfLabel}</Button>
                      </div>
                    );
                  })()}
                </div>
              </CardHeader>

              {selectedVendor && (
                <>
                  <div className="px-6 pb-4 flex gap-3 flex-wrap items-center">
                    <div className="relative flex-1 min-w-[200px]">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input placeholder="Search by company, bank, account, UPI, region..." value={bankFilters.search} onChange={(e) => setBankFilters({ ...bankFilters, search: e.target.value })} className="pl-9" data-testid="bank-search" />
                    </div>
                    <Select value={bankFilters.status || "all-status"} onValueChange={(val) => setBankFilters({ ...bankFilters, status: val === "all-status" ? "" : val })}>
                      <SelectTrigger className="w-44"><SelectValue placeholder="Status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all-status">All Status</SelectItem>
                        {['New Bank', 'In Process', 'Unassigned', 'Active', 'Inactive', 'Freezed - Transaction', 'Freezed - CBI', 'Closed'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {(() => {
                      const branches = [...new Set(selectedVendor.banks.map(b => b.assignedPartnerName).filter(Boolean))].sort();
                      return branches.length > 0 ? (
                        <Select value={bankFilters.branch || "all-branch"} onValueChange={(val) => { setBankFilters({ ...bankFilters, branch: val === "all-branch" ? "" : val }); if (val !== "all-branch") setSelectedBranch(val); else setSelectedBranch(null); }}>
                          <SelectTrigger className="w-44" data-testid="branch-filter"><SelectValue placeholder="Branch" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all-branch">All Branches</SelectItem>
                            {branches.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : null;
                    })()}
                    {isAdmin && (
                      <Select value={bankFilters.pricingStatus || "all-pricing"} onValueChange={(val) => setBankFilters({ ...bankFilters, pricingStatus: val === "all-pricing" ? "" : val })}>
                        <SelectTrigger className="w-40"><SelectValue placeholder="Pricing" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all-pricing">All Pricing</SelectItem>
                          <SelectItem value="missing">Missing</SelectItem>
                          <SelectItem value="set">Set</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    <Select value={bankFilters.qrFilter || "all-qr"} onValueChange={(val) => setBankFilters({ ...bankFilters, qrFilter: val === "all-qr" ? "" : val })}>
                      <SelectTrigger className="w-36" data-testid="qr-filter"><SelectValue placeholder="QR" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all-qr">All QR</SelectItem>
                        <SelectItem value="with-qr">With QR</SelectItem>
                        <SelectItem value="without-qr">Without QR</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={bankSort} onValueChange={setBankSort}>
                      <SelectTrigger className="w-44" data-testid="bank-sort"><SelectValue placeholder="Sort" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="companyName-asc">Company A-Z</SelectItem>
                        <SelectItem value="companyName-desc">Company Z-A</SelectItem>
                        <SelectItem value="bankName-asc">Bank A-Z</SelectItem>
                        <SelectItem value="status-asc">Status A-Z</SelectItem>
                        <SelectItem value="createdAt-desc">Newest First</SelectItem>
                        <SelectItem value="createdAt-asc">Oldest First</SelectItem>
                        <SelectItem value="accountNumber-asc">Acc No Asc</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className={`px-6 pb-4 grid ${isAdmin ? 'grid-cols-2 md:grid-cols-5' : 'grid-cols-2 md:grid-cols-4'} gap-3`}>
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-100"><div className="text-xs text-blue-600 font-semibold">Total</div><div className="text-2xl font-bold text-blue-700 mt-1">{bankCounts.total}</div></div>
                    <div className="p-3 bg-green-50 rounded-lg border border-green-100"><div className="text-xs text-green-600 font-semibold">Active</div><div className="text-2xl font-bold text-green-700 mt-1">{bankCounts.active}</div></div>
                    <div className="p-3 bg-red-50 rounded-lg border border-red-100"><div className="text-xs text-red-600 font-semibold">Freezed</div><div className="text-2xl font-bold text-red-700 mt-1">{bankCounts.freezed}</div></div>
                    <div className="p-3 bg-red-50 rounded-lg border border-red-100"><div className="text-xs text-red-600 font-semibold">Freezed Amt</div><div className="text-lg font-bold text-red-700 mt-1">{formatMoney(bankCounts.freezedAmount)}</div></div>
                    {isAdmin && <div className="p-3 bg-amber-50 rounded-lg border-2 border-amber-400"><div className="text-xs text-amber-800 font-bold">Pricing Missing</div><div className="text-2xl font-bold text-amber-700 mt-1">{bankCounts.pricingMissing}</div></div>}
                  </div>

                  <CardContent className="p-0">
                    <BankTable
                      banks={filteredBanks} isAdmin={isAdmin} checkPricingMissing={checkPricingMissing}
                      onViewBank={(bank) => { setSelectedBank(bank); setShowBankDetailDialog(true); }}
                      onDeleteBank={handleDeleteBank}
                    />
                  </CardContent>
                </>
              )}
            </Card>
          </div>
        </div>
      </div>

      <Dialog open={showVendorDialog} onOpenChange={setShowVendorDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingVendor ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle></DialogHeader>
          <form onSubmit={handleVendorSubmit}>
            <div className="space-y-4"><div><Label htmlFor="vendorName">Vendor Name *</Label><Input id="vendorName" value={vendorForm.name} onChange={(e) => setVendorForm({ name: e.target.value })} required /></div></div>
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setShowVendorDialog(false)}>Cancel</Button>
              <Button type="submit">{editingVendor ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AddBankDialogEnhanced open={showBankDialog} onOpenChange={setShowBankDialog} />
      <BankDetailDialogEnhanced open={showBankDetailDialog} onOpenChange={setShowBankDetailDialog} />
      <UploadBankDialog open={showUploadDialog} onOpenChange={setShowUploadDialog} />
      <PinDialog
        open={showPinDialog}
        onOpenChange={(open) => { setShowPinDialog(open); if (!open) setPendingDelete(null); }}
        onConfirm={handlePinConfirm} loading={pinLoading}
        title="Confirm Delete" description="Enter your 4-digit Admin PIN to confirm this deletion."
      />
    </>
  );
};

export default VendorsPage;
